/**
 * Microdose patients who need a clinician now (workbook v1.2: OPT-HE-007 "next step shows as ready for review",
 * OPT-TC-013 "provider sees it pending", and the check-in alerts of tabs 08 and 10), for the clinic dashboard.
 *
 * One row per patient with what is waiting: a step ready for review, the ceiling available, a change or a new vial
 * the patient has not confirmed in 3 days, open check-in alerts. Labels only, never a score. Each row opens the
 * patient's dose card. Renders nothing while nobody needs anything, so it costs the dashboard no space.
 *
 * Rendered by the dashboard: <MicrodoseQueueCard /> (pages/Dashboard.tsx, under ProtocolNotices).
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { InfoHint } from './ui';
import { IconChevron, IconSyringe } from './icons';

interface QueueRow {
  patientId: string;
  name: string;
  drugName: string;
  currentText: string;
  reasons: Array<'ready' | 'ceiling_available' | 'not_confirmed' | 'vial_not_confirmed' | 'alerts'>;
  nextText: string | null;
  since: string | null;
  alerts: { red: number; orange: number; yellow: number };
}

const REASON: Record<QueueRow['reasons'][number], { text: (r: QueueRow) => string; tone: string }> = {
  ready: { text: (r) => `Step to ${r.nextText} mg ready for review`, tone: 'bg-mint text-forest' },
  ceiling_available: { text: (r) => `Ceiling step to ${r.nextText} mg available`, tone: 'bg-mint text-forest' },
  not_confirmed: { text: () => 'Dose change not confirmed in 3 days', tone: 'bg-risk-orange-soft text-risk-orange' },
  vial_not_confirmed: { text: () => 'New vial not confirmed in 3 days', tone: 'bg-risk-orange-soft text-risk-orange' },
  alerts: { text: () => 'Open check-in alerts', tone: 'bg-risk-yellow-soft text-risk-yellow' },
};

/** The chip takes the colour of the most serious open alert: RED is an urgent clinician assessment (OPT-HE-004). */
function alertTone(a: QueueRow['alerts']): string {
  return a.red ? 'bg-alert-soft text-alert' : a.orange ? 'bg-risk-orange-soft text-risk-orange' : 'bg-risk-yellow-soft text-risk-yellow';
}

function alertText(a: QueueRow['alerts']): string {
  return [a.red ? `${a.red} RED` : null, a.orange ? `${a.orange} ORANGE` : null, a.yellow ? `${a.yellow} YELLOW` : null].filter(Boolean).join(' · ');
}

export function MicrodoseQueueCard() {
  const query = useQuery({ queryKey: ['microdose', 'queue'], queryFn: () => api.get<{ rows: QueueRow[] }>('/microdose/queue') });
  const rows = query.data?.rows ?? [];
  if (query.isLoading || query.error || rows.length === 0) return null;
  return (
    <section className="mb-[13px] rounded-[12px] border border-hairline bg-white" data-testid="microdose-queue">
      <div className="flex items-center gap-2 px-[18px] pb-2 pt-3.5">
        <IconSyringe size={18} className="text-forest" />
        <h2 className="font-display text-[20px] leading-tight text-navy" style={{ fontWeight: 560 }}>
          Microdose dose steps ({rows.length})
        </h2>
        <InfoHint label="About this list">
          Patients on a signed microdose ladder who need a clinician: a step ready for review, the ceiling available, a
          change or a new vial not confirmed in 3 days, or an open check-in alert. Open a patient to decide on their dose card.
        </InfoHint>
      </div>
      <ul className="divide-y divide-hairline/70 pb-1">
        {rows.map((r) => (
          <li key={r.patientId}>
            <Link
              to={`/patients/${r.patientId}/workspace`}
              className="flex items-center gap-3 px-[18px] py-3 transition-colors hover:bg-paper"
              data-testid={`microdose-queue-${r.patientId}`}
            >
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-display text-[17px] leading-tight text-navy" style={{ fontWeight: 560 }}>
                    {r.name}
                  </span>
                  <span className="text-[13px] text-ink-muted">
                    {r.drugName} {r.currentText} mg weekly
                    {r.since ? ` · ${r.reasons.includes('not_confirmed') || r.reasons.includes('vial_not_confirmed') ? 'waiting since' : 'check-in'} ${formatDate(r.since)}` : ''}
                  </span>
                </span>
                <span className="mt-1.5 flex flex-wrap gap-1.5">
                  {r.reasons.map((reason) => (
                    <span key={reason} className={`rounded-[5px] px-2 py-[3px] text-[12px] font-semibold leading-none ${reason === 'alerts' ? alertTone(r.alerts) : REASON[reason].tone}`}>
                      {REASON[reason].text(r)}
                      {reason === 'alerts' ? `: ${alertText(r.alerts)}` : ''}
                    </span>
                  ))}
                </span>
              </span>
              <IconChevron size={18} className="shrink-0 text-navy" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
